import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter, debounceTime, finalize } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Feature, FeatureStatus } from '../../models/feature-management.models';
import { FeatureManagementService } from '../../services/feature-management.service';
import { MatDialog } from '@angular/material/dialog';
import { FeatureFlagService, ToastService } from '@overtur-monorepo/keying-shared';

@Component({
  selector: 'app-feature-list',
  templateUrl: './feature-list.component.html',
  styleUrls: ['./feature-list.component.scss'],
  standalone: false,
})
export class FeatureListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'status',
    'name',
    // 'rolloutPercentage', // Commented out - not using gradual rollout currently (future use)
    'accessSummary',
    'modifiedDate',
    'actions',
  ];

  dataSource = new MatTableDataSource<Feature>([]);
  loading = false;
  totalCount = 0;
  pageSize = 10;
  currentPage = 1;

  // Filters
  searchText = '';
  selectedStatus: number | null = null; // Single status filter (chip-based)
  selectedCategory = '';
  categories: string[] = [];
  statuses = [
    { id: 1, name: 'Disabled' },
    { id: 2, name: 'Limited Access' },
    { id: 3, name: 'Full Access' },
  ];

  private destroy$ = new Subject<void>();
  searchSubject = new Subject<string>();
  private accessSummaryCache = new Map<string, { roles: string; roleValues: string; users: string; fullUsers: string; additionalCount: number; summary?: string }>();

  constructor(
    private featureService: FeatureManagementService,
    private router: Router,
    private dialog: MatDialog,
    private toastService: ToastService,
    private featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Restore page size from local storage
    const savedPageSize = localStorage.getItem('featureListPageSize');
    if (savedPageSize) {
      this.pageSize = parseInt(savedPageSize, 10);
    }

    this.loadCategories();

    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadFeatures();
      });

    // Configure custom sorting for date columns
    this.dataSource.sortingDataAccessor = (item: Feature, property: string) => {
      switch (property) {
        case 'modifiedDate':
          return item.modifiedOn ? new Date(item.modifiedOn).getTime() : 0;
        case 'activatedOn':
          return item.activatedOn ? new Date(item.activatedOn).getTime() : 0;
        case 'createdDate':
          return new Date(item.createdOn).getTime();
        case 'name':
          return item.name.toLowerCase();
        case 'status':
          return item.statusId;
        case 'rolloutPercentage':
          return item.rolloutPercentage;
        case 'accessSummary':
          return (item as any).accessSummary?.toLowerCase() || '';
        default:
          return (item as any)[property];
      }
    };

    this.loadFeatures();

    // Reload features when navigating back to this route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => {
          // Only reload if we're on the features list page (not the editor)
          const url = event.urlAfterRedirects;
          return url.endsWith('/feature-management/features') || url === '/feature-management/features';
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadFeatures();
      });
  }

  ngAfterViewInit(): void {
    // Connect sort to datasource (paginator is server-side, not connected to dataSource)
    this.dataSource.sort = this.sort;

    // Set default sort to most recent first
    if (this.sort) {
      setTimeout(() => {
        this.sort.active = 'modifiedDate';
        this.sort.direction = 'desc';
        this.dataSource.sort = this.sort;
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFeatures(): void {
    this.loading = true;
    const filters = {
      statusId: this.selectedStatus ?? undefined,
      category: this.selectedCategory || undefined,
      searchTerm: this.searchText.trim() || undefined,
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
    };

    this.featureService
      .getFeatures(filters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.accessSummaryCache.clear();
          this.dataSource.data = response.items ?? [];
          this.totalCount = response.totalCount ?? 0;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          console.error('Error loading features:', error);
          this.cdr.detectChanges();
        },
      });
  }

  loadCategories(): void {
    // TODO: Enable when backend endpoint is implemented
    // For now, use hardcoded categories
    this.categories = ['UI', 'API', 'Integration', 'Security', 'Performance', 'Other'];

    // this.featureService
    //   .getCategories()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe({
    //     next: (categories) => {
    //       this.categories = categories;
    //     },
    //     error: (error) => {
    //       console.error('Error loading categories:', error);
    //     },
    //   });
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.loadFeatures();
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = null;
    this.selectedCategory = '';
    this.currentPage = 1;
    this.dataSource.filter = '';
    this.loadFeatures();
  }

  onPageChange(event: any): void {
    const pageSizeChanged = event.pageSize !== this.pageSize;
    this.pageSize = event.pageSize;
    if (pageSizeChanged) {
      this.currentPage = 1;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    } else {
      this.currentPage = event.pageIndex + 1;
    }
    localStorage.setItem('featureListPageSize', this.pageSize.toString());
    this.loadFeatures();
  }

  toggleStatus(statusId: number): void {
    // Toggle: if already selected, deselect; otherwise select
    if (this.selectedStatus === statusId) {
      this.selectedStatus = null;
    } else {
      this.selectedStatus = statusId;
    }
    this.applyFilter();
  }

  isStatusActive(statusId: number): boolean {
    return this.selectedStatus === statusId;
  }

  getStatus(statusId: number): FeatureStatus {
    const statusMap: Record<number, FeatureStatus> = {
      1: 'Disabled',
      2: 'Limited Access',
      3: 'Full Access',
    };
    return statusMap[statusId] || 'Disabled';
  }

  getStatusIcon(statusId: number): string {
    const status = this.getStatus(statusId);
    const icons: Record<FeatureStatus, string> = {
      'Disabled': 'cancel',
      'Limited Access': 'flight_takeoff',
      'Full Access': 'check_circle',
    };
    return icons[status] || 'help';
  }

  getStatusColor(statusId: number): string {
    const status = this.getStatus(statusId);
    const colors: Record<FeatureStatus, string> = {
      'Disabled': '', // Custom gray styling via CSS
      'Limited Access': '', // Custom orange styling via CSS
      'Full Access': 'primary',
    };
    return colors[status] || '';
  }

  /**
   * Determines if a feature is actually active right now
   * Note: Status now always reflects current reality (updated by background job)
   */
  isFeatureActiveNow(feature: Feature): boolean {
    // Status always reflects current reality now
    // No need to check activatedOn (historical field)
    // Just check if status is enabled or pilot
    return feature.statusId === 2 || feature.statusId === 3;
  }

  /**
   * Gets the status description from a status ID
   */
  getStatusDescriptionFromId(statusId: number): string {
    const statusMap: Record<number, string> = {
      1: 'Disabled',
      2: 'Limited Access',
      3: 'Full Access',
      4: 'Archived',
    };
    return statusMap[statusId] || 'Unknown';
  }

  /**
   * Gets the effective status text including schedule information
   */
  getEffectiveStatusText(feature: Feature): string {
    // Use our frontend mapping instead of backend statusDescription to show "Limited Access" instead of "Pilot"
    return this.getStatusDescriptionFromId(feature.statusId);
  }

  /**
   * Parse accessSummary and return roles and users separately
   * Also cleans up user emails by removing @allegion.com and replacing periods with spaces
   * Limits display to first 3 users and shows "+X more" if there are additional users
   */
  parseAccessSummary(accessSummary: string | null | undefined): { roles: string; roleValues: string; users: string; fullUsers: string; additionalCount: number; summary?: string } {
    if (!accessSummary) {
      return { roles: '', roleValues: '', users: '', fullUsers: '', additionalCount: 0 };
    }
    const cached = this.accessSummaryCache.get(accessSummary);
    if (cached) return cached;

    const specialCases: Record<string, string> = {
      'All Users': 'All KeyCom Users',
      'None': 'No specific access configured',
    };
    if (specialCases[accessSummary]) {
      const result = { roles: '', roleValues: '', users: '', fullUsers: '', additionalCount: 0, summary: specialCases[accessSummary] };
      this.accessSummaryCache.set(accessSummary, result);
      return result;
    }

    // Split by semicolon to separate users from roles
    const parts = accessSummary.split(';').map(p => p.trim());
    let roles = '';
    let roleValues = '';
    let users = '';
    let fullUsers = '';
    let additionalCount = 0;

    parts.forEach(part => {
      if (part.startsWith('Roles:')) {
        roles = part; // "Roles: SuperUser, Admin"
        roleValues = part.replace(/^Roles:\s*/i, ''); // "SuperUser, Admin"
      } else {
        // Process user emails: remove @allegion.com and replace periods with spaces
        const emails = part.split(',').map(email => email.trim());
        const cleanedEmails = emails.map(email => {
          // Remove @allegion.com domain
          const cleanEmail = email.replace(/@allegion\.com/i, '');
          // Replace periods with spaces
          return cleanEmail.replace(/\./g, ' ');
        });

        fullUsers = cleanedEmails.join(', '); // All users for tooltip

        // Show only first 3 users
        if (cleanedEmails.length > 3) {
          users = cleanedEmails.slice(0, 3).join(', ');
          additionalCount = cleanedEmails.length - 3;
        } else {
          users = fullUsers;
          additionalCount = 0;
        }
      }
    });

    const result = { roles, roleValues, users, fullUsers, additionalCount };
    this.accessSummaryCache.set(accessSummary, result);
    return result;
  }

  /**
   * Get formatted tooltip for access summary
   */
  getAccessTooltip(accessSummary: string | null | undefined): string {
    if (!accessSummary) {
      return 'No targeting rules configured';
    }

    const parsed = this.parseAccessSummary(accessSummary);
    const parts: string[] = [];

    if (parsed.roles) {
      parts.push(parsed.roles);
    }

    if (parsed.fullUsers) {
      parts.push(`Users: ${parsed.fullUsers}`);
    }

    return parts.length > 0 ? parts.join('\n') : accessSummary;
  }

  createFeature(): void {
    this.router.navigate(['/feature-management/features/new']);
  }

  editFeature(feature: Feature): void {
    this.router.navigate(['/feature-management/features', feature.id]);
  }

  setFullAccess(feature: Feature, event: Event): void {
    event.stopPropagation();
    this.loading = true;
    this.featureService
      .setFullAccess(feature.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.featureFlagService.refreshAll();
          this.loadFeatures();
        },
        error: (error) => {
          console.error('Error enabling feature:', error);
          this.loading = false;
          const errorMessage = error?.error?.message || error?.message || 'Failed to enable feature';
          this.toastService.showToast({
            title: 'Error',
            subTitle: errorMessage,
            type: 'error',
          });
        },
      });
  }

  setDisabled(feature: Feature, event: Event): void {
    event.stopPropagation();
    this.loading = true;
    this.featureService
      .setDisabled(feature.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.featureFlagService.refreshAll();
          this.loadFeatures();
        },
        error: (error) => {
          console.error('Error disabling feature:', error);
          this.loading = false;
          const errorMessage = error?.error?.message || error?.message || 'Failed to disable feature';
          this.toastService.showToast({
            title: 'Error',
            subTitle: errorMessage,
            type: 'error',
          });
        },
      });
  }

  setLimitedAccess(feature: Feature, event: Event): void {
    event.stopPropagation();
    this.loading = true;
    this.featureService
      .setLimitedAccess(feature.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.featureFlagService.refreshAll();
          this.loadFeatures();
        },
        error: (error) => {
          console.error('Error setting pilot mode:', error);
          this.loading = false;
          const errorMessage = error?.error?.message || error?.message || 'Failed to set pilot mode';
          this.toastService.showToast({
            title: 'Error',
            subTitle: errorMessage,
            type: 'error',
          });
        },
      });
  }

}
